import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileUser, ChevronRight, BookOpen, FileText, PenTool, CheckCircle, Sparkles } from 'lucide-react';

const CV_SECTIONS = [
  {
    id: 'structure',
    title: 'CV-struktur',
    icon: FileText,
    description: 'Så bygger du ett professionellt CV.',
    content: [
      '**Kontaktuppgifter:** Namn, telefon, e-post, LinkedIn, ort (inte full adress).',
      '**Sammanfattning/Profil:** 2-3 meningar om vem du är och vad du söker. Anpassa till varje tjänst.',
      '**Arbetslivserfarenhet:** Omvänd kronologisk ordning. Fokusera på resultat, inte arbetsuppgifter.',
      '**Utbildning:** Relevant utbildning. Inkludera kurser om de är relevanta.',
      '**Kompetenser:** Hårda skills (program, språk) och mjuka skills (ledarskap, kommunikation).',
      '**Övrigt:** Språk, körkort, ideellt arbete om relevant.',
      '**Längd:** Max 2 sidor. Helst 1 för juniora roller.'
    ],
    tips: [
      'Använd bullet points för läsbarhet',
      'Kvantifiera resultat: "Ökade försäljningen med 25%"',
      'Anpassa CV:t för varje tjänst',
      'Undvik stavfel – låt någon annan korrekturläsa'
    ]
  },
  {
    id: 'letter',
    title: 'Personligt brev',
    icon: PenTool,
    description: 'Skriv ett brev som sticker ut.',
    content: [
      '**Inledning:** Fånga intresset direkt. Undvik "Jag söker tjänsten som..."',
      '**Varför företaget?** Visa att du researcht dem. Vad attraherar dig?',
      '**Varför du?** Koppla din erfarenhet till tjänstens krav. Ge exempel.',
      '**Avslutning:** Visa entusiasm. Be om intervju. Tacka för deras tid.',
      '**Längd:** Max 1 sida. 3-4 stycken.',
      '**Ton:** Professionell men personlig. Undvik klichéer.'
    ],
    tips: [
      'Börja ALDRIG med "Jag"',
      'Nämn företagets namn – visar att du inte massansöker',
      'Berätta en kort historia som visar din kompetens',
      'Avsluta med ett "call to action"'
    ]
  },
  {
    id: 'mistakes',
    title: 'Vanliga misstag',
    icon: CheckCircle,
    description: 'Fel som kan kosta dig jobbet.',
    content: [
      '**Stavfel och grammatik:** Visar bristande noggrannhet. Korrekturläs!',
      '**Generiskt CV:** "Jag är driven och social" säger inget. Var specifik.',
      '**Fel kontaktuppgifter:** Kolla att telefon och e-post stämmer.',
      '**Oprofessionell e-post:** party_lisa@... → lisa.svensson@...',
      '**Ljuga:** Överdrifter upptäcks. Var ärlig om din erfarenhet.',
      '**Dåligt foto:** Inget foto alls är bättre än ett selfie eller festbild.',
      '**Irrelevant info:** Skippa grundskola och sommarjobb från 15 år sedan.'
    ],
    tips: [
      'Be någon läsa igenom innan du skickar',
      'Googla dig själv – vad ser rekryteraren?',
      'Uppdatera LinkedIn så det matchar CV:t'
    ]
  },
  {
    id: 'interview',
    title: 'Intervjuförberedelse',
    icon: Sparkles,
    description: 'Så förbereder du dig för intervjun.',
    content: [
      '**Researcha företaget:** Vad gör de? Värderingar? Senaste nyheterna?',
      '**Förbered svar:** Berätta om dig själv, styrkor/svagheter, varför detta jobb?',
      '**STAR-metoden:** Situation, Task, Action, Result – för beteendefrågor.',
      '**Förbered frågor:** Visa intresse genom att fråga om teamet, kulturen, utvecklingsmöjligheter.',
      '**Praktiskt:** Klädsel (hellre för fin än för casual), kom i tid, ta med extra CV.',
      '**Kroppsspråk:** Ögonkontakt, leende, fast handslag, sitt rakt.'
    ],
    tips: [
      'Öva högt – inte bara i huvudet',
      'Förbered "Berätta om dig själv" till 2 minuter',
      'Ha konkreta exempel redo',
      'Skicka tackmail inom 24 timmar'
    ]
  },
  {
    id: 'examples',
    title: 'Exempel på formuleringar',
    icon: BookOpen,
    description: 'Bra vs dåliga formuleringar.',
    content: [
      '**DÅLIGT:** "Ansvarade för kundtjänst"',
      '**BRA:** "Hanterade 50+ kundärenden dagligen med 95% nöjdhet"',
      '',
      '**DÅLIGT:** "Teamplayer med god kommunikationsförmåga"',
      '**BRA:** "Ledde ett projekt med 5 teammedlemmar som levererade 2 veckor före deadline"',
      '',
      '**DÅLIGT:** "Jag är driven och vill utvecklas"',
      '**BRA:** "Under det senaste året har jag certifierat mig i X och Y för att bredda min kompetens"',
      '',
      '**DÅLIGT:** "Arbetade med försäljning"',
      '**BRA:** "Överträffade försäljningsmålen med 20% tre kvartal i rad"'
    ],
    tips: [
      'Använd aktiva verb: ledde, utvecklade, ökade, minskade',
      'Kvantifiera när möjligt',
      'Fokusera på resultat, inte aktiviteter'
    ]
  }
];

export default function CvHelpPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [selectedSection, setSelectedSection] = useState(null);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="cv-help-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">CV-hjälp</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till CV-hjälp.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (selectedSection) {
    const section = CV_SECTIONS.find(s => s.id === selectedSection);
    return (
      <div className="min-h-screen bg-background" data-testid="cv-help-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedSection(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">CV-hjälp</h1>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-teal-50 rounded-2xl p-8">
            <section.icon className="w-10 h-10 text-teal-600 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{section.title}</h2>
            <p className="text-muted-foreground">{section.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            {section.content.map((text, i) => (
              <p key={i} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </div>

          <div className="bg-teal-50 rounded-2xl p-6 space-y-3">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-teal-600" />
              Tips
            </h3>
            <ul className="space-y-2">
              {section.tips.map((tip, i) => (
                <li key={i} className="flex gap-2 items-start">
                  <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-teal-800">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="cv-help-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">CV-hjälp</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-2">
          <FileUser className="w-12 h-12 mx-auto text-teal-500" />
          <h2 className="font-heading text-2xl font-semibold">CV-hjälp</h2>
          <p className="text-muted-foreground">Guider för CV och personligt brev</p>
        </div>

        <div className="space-y-3">
          {CV_SECTIONS.map((section) => (
            <button
              key={section.id}
              onClick={() => setSelectedSection(section.id)}
              className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center">
                  <section.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{section.title}</h3>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
              </div>
            </button>
          ))}
        </div>

        {/* Quick checklist */}
        <div className="bg-teal-50 rounded-2xl p-4 border border-teal-100">
          <h3 className="font-semibold text-teal-800 mb-3">CV-checklista</h3>
          <div className="grid gap-2 md:grid-cols-2">
            <div className="flex items-center gap-2 text-sm text-teal-700">
              <CheckCircle className="w-4 h-4" /> Kontaktuppgifter uppdaterade
            </div>
            <div className="flex items-center gap-2 text-sm text-teal-700">
              <CheckCircle className="w-4 h-4" /> Anpassat till tjänsten
            </div>
            <div className="flex items-center gap-2 text-sm text-teal-700">
              <CheckCircle className="w-4 h-4" /> Inga stavfel
            </div>
            <div className="flex items-center gap-2 text-sm text-teal-700">
              <CheckCircle className="w-4 h-4" /> Max 2 sidor
            </div>
            <div className="flex items-center gap-2 text-sm text-teal-700">
              <CheckCircle className="w-4 h-4" /> Resultat, inte bara uppgifter
            </div>
            <div className="flex items-center gap-2 text-sm text-teal-700">
              <CheckCircle className="w-4 h-4" /> PDF-format
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
